import { useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { EIconSize } from 'utils/constants/app/ui';
import {
  competitions,
  events,
  gameList,
  markets,
  regions
} from 'utils/constants/sportsbook/data-fields';
import { GAME_TYPE_LIVE } from 'utils/constants/sportsbook/generic';
import { mountPoint } from 'utils/generic/route-manipulation';
import RidGenerator from 'utils/swarm/rid-generator';
import { Region } from 'interfaces/sportsbook-data-levels';
import { executeCommand } from 'services/get-data-level';
import { LiveCompetitionsCollapseSkeleton } from './Skeleton';
import { AdvancedMatchCardInLineWrapper } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMatchCardInLineWrapper';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import { useMounted } from 'hooks/useMounted';
import { useCustomHistory } from 'newhooks/generic/useCustomHistory';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { setAdvancedLiveGames } from 'store/actions/new-sportsbook';
import { getAdvancedLiveGames } from 'store/selectors/new-sportsbook';

type TLiveCompetitionsCollapseProps = {
  sportAlias: string;
};

export const LiveCompetitionsCollapse = ({
  sportAlias
}: TLiveCompetitionsCollapseProps) => {
  const dispatch = useDispatch();
  const { mounted } = useMounted();
  const { params } = useSbRouteMatch();
  const { push } = useCustomHistory();

  const liveGamesData = useSelector(getAdvancedLiveGames)?.[sportAlias];

  const handleMatchCardClick = ({
    sportAlias,
    regionAlias,
    compId,
    gameId
  }: {
    sportAlias: string;
    regionAlias: string;
    compId: number;
    gameId: number;
  }) => {
    const redirectPath = `${mountPoint(
      window.getPathname()
    )}/${sportAlias}/${regionAlias}/${compId}/${gameId}${
      window.location.search
    }`;

    push(redirectPath);
  };

  useLayoutEffect(() => {
    const rId = RidGenerator.gForSubscribe(`ADVANCED_GET_LIVE_GAMES`);

    const what = {
      ...regions,
      ...competitions,
      ...gameList,
      ...markets,
      ...events
    };

    const where = {
      game: { type: GAME_TYPE_LIVE },
      sport: { alias: sportAlias },
      market: {
        display_key: {
          '@in': ['WINNER']
        },
        display_sub_key: {
          '@in': ['MATCH']
        }
      }
    };

    const onResponse = (data: { region: Record<string, Region> }) => {
      if (data && data.region && mounted.current) {
        dispatch(setAdvancedLiveGames({ [sportAlias]: data.region }));
      }
    };

    executeCommand(rId, what, where, onResponse, true, onResponse);
  }, [sportAlias]);

  return liveGamesData ? (
    sortByOrder(Object.values(liveGamesData)).map(
      (region, regionIndex) =>
        region.competition &&
        sortByOrder(Object.values(region.competition)).map(comp => (
          <NavigationCollapse
            key={comp.id}
            defaultActive={
              params.competition === String(comp.id) || regionIndex === 0
            }
            headerClassName="liveRegionCollapseHeader"
            wrapperClassName="liveRegionCollapseWrapper"
            icon={
              <GlobalIcon
                lib="flags"
                name={region.alias}
                size={EIconSize._24}
              />
            }
            title={`${region.name} - ${comp.name}`}
          >
            {comp.game &&
              sortByOrder(Object.values(comp.game), 'start_ts').map(game => (
                <AdvancedMatchCardInLineWrapper
                  key={game.id}
                  game={game}
                  currentSport={sportAlias}
                  competitionName={comp.name}
                  handleMatchCardClick={() =>
                    handleMatchCardClick({
                      sportAlias,
                      regionAlias: region.alias,
                      compId: comp.id,
                      gameId: game.id
                    })
                  }
                  longDate
                />
              ))}
          </NavigationCollapse>
        ))
    )
  ) : (
    <LiveCompetitionsCollapseSkeleton />
  );
};
